import { useDroppable } from '@dnd-kit/core';
import type { CSSProperties } from 'react';

import type { PipelineColumn as PipelineColumnDto } from '@/pages/pipelines/common/types';

import { PipelineColumnCards } from './column-cards';
import { PipelineColumnHeader } from './column-header';
import { CardsStack, ColumnShell } from './styled';

type Props = {
  column: PipelineColumnDto;
  stageColor: string;
  stageStyle: CSSProperties;
  onSelectCard: (_cardId: string) => void;
  dragAvailable: boolean;
  dragInteractionDisabled: boolean;
  stageFiltered: boolean;
  sortActive: boolean;
  dragSourceStage?: string | null;
  onToggleStageFilter?: (_stage: string) => void;
  onToggleSort?: () => void;
  onAddCard?: (_stage: string) => void;
};

export const PipelineExpandedColumn = ({
  column,
  stageColor,
  stageStyle,
  onSelectCard,
  dragAvailable,
  dragInteractionDisabled,
  stageFiltered,
  sortActive,
  dragSourceStage,
  onToggleStageFilter,
  onToggleSort,
  onAddCard,
}: Props) => {
  // Same-column reorder is not supported, so the drag's source column must not
  // act as a drop target.
  const isDragSource = dragSourceStage === column.stage;
  const { setNodeRef, isOver } = useDroppable({
    id: column.stage,
    disabled: !dragAvailable || dragInteractionDisabled || isDragSource,
  });

  return (
    <ColumnShell
      data-testid={`pipeline-column-expanded-${column.stage}`}
      data-terminal={column.terminal}
      style={stageStyle}
    >
      <PipelineColumnHeader
        column={column}
        stageColor={stageColor}
        stageFiltered={stageFiltered}
        sortActive={sortActive}
        onToggleStageFilter={onToggleStageFilter}
        onToggleSort={onToggleSort}
        onAddCard={onAddCard}
      />

      <CardsStack
        ref={dragAvailable ? setNodeRef : undefined}
        data-testid="pipeline-cards-stack"
        data-dragging-over={!isDragSource && isOver}
      >
        <PipelineColumnCards
          column={column}
          dragAvailable={dragAvailable}
          dragInteractionDisabled={dragInteractionDisabled}
          onSelectCard={onSelectCard}
          onAddCard={onAddCard}
        />
      </CardsStack>
    </ColumnShell>
  );
};
